import { useCallback, useState } from 'react'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import { useMessage } from '../pageView/Page'
import { exportData } from '../../../../common/model/export/exportData.js'
import { formatData } from '../../../../common/model/export/formatData.js'

/** Not a declared profile: the columns the table is showing right now. */
export const VISIBLE_COLUMNS = '__visible__'

/**
 * Runs a list export (task 35.01 §8).
 *
 * Export is not a bulk action: bulk actions post once per selected row and
 * refuse to run with an empty selection, while an export with nothing selected
 * is the common case — the whole list. So it has its own hook rather than
 * another branch inside useListBulkActions.
 *
 * The scope is either the selected ids or the query the list is already
 * running. Forwarding that query, minus pagination, is what makes "export all"
 * honour whatever filters and search the user has applied without export
 * needing to understand any of them.
 */
/**
 * listViewData column types -> export field types. A column with no type is
 * text, which is what the table renders it as.
 */
const TYPE_FROM_COLUMN = {
    date: 'date',
    datetime: 'datetime',
    amount: 'amount',
    quantity: 'number',
    number: 'number',
    boolean: 'boolean',
    bookedNumber: 'text',
    merge: 'text',
    url: 'text'
}

const useListExport = (endpoint, entity, visibleColumns = [], entityLabelKey = null) => {
    const { postMeFile } = useApi()
    const { t } = useTranslation()
    const [, setMessage] = useMessage((s) => s)
    const [isExporting, setIsExporting] = useState(false)

    /** Formats the export dialog can offer, straight from the shared registry. */
    const formats = Object.entries(formatData).map(([id, format]) => ({
        id,
        label: format.label
    }))

    /**
     * Profiles the dialog offers: those declared for the entity, plus the
     * on-screen columns when the list has any. Offered last, because a curated
     * profile is usually the better answer.
     */
    const profiles = [
        ...Object.entries(exportData[entity] || {}).map(([name, profile]) => ({
            name,
            label: profile.label
        })),
        ...(visibleColumns?.length ? [{ name: VISIBLE_COLUMNS, label: 'export.profile.visible' }] : [])
    ]

    /**
     * Header text for the columns of the profile being exported. The server has
     * no translation files, so the labels are resolved here and sent along.
     */
    const buildHeaders = useCallback((profileName) => {
        const profile = exportData[entity]?.[profileName || 'default']
        if (!profile) return undefined

        return profile.fields.reduce((headers, field) => {
            if (field.label) headers[field.field] = t(field.label)
            return headers
        }, {})
    }, [entity, t])

    /**
     * Tier 3: the columns on screen right now (35.01 §4).
     *
     * `visibleColumns` is what ViewTable is actually rendering — the table's
     * columns after `dropOrder` has removed any the window is too narrow for —
     * so "export what I am looking at" means exactly that, including the user's
     * current screen width. Labels are resolved here, as for any other profile.
     */
    const currentColumns = useCallback(() => (visibleColumns || [])
        .filter(column => column?.id)
        .map(column => ({
            field: column.id,
            type: TYPE_FROM_COLUMN[column.type] || 'text',
            label: column.label ? t(column.label) : column.id
        })), [visibleColumns, t])

    const runExport = useCallback(async ({
        format = 'csv',
        profile = 'default',
        scope,
        selectedIds = [],
        query = {},
        options = {}
    } = {}) => {
        setIsExporting(true)

        try {
            const useSelection = scope === 'selection' && selectedIds.length > 0

            // The pseudo-profile the dialog offers alongside the declared ones.
            const useVisible = profile === VISIBLE_COLUMNS
            const columns = useVisible ? currentColumns() : undefined

            const body = {
                profile: useVisible ? undefined : profile,
                ...(columns?.length ? { columns } : {}),
                format,
                scope: useSelection
                    ? { type: 'selection', ids: selectedIds }
                    : { type: 'query', query },
                options: {
                    locale: navigator.language || 'en-US',
                    // Names the download in the user's language; the server only
                    // makes it filesystem-safe.
                    entityLabel: entityLabelKey ? t(entityLabelKey) : undefined,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    headers: useVisible
                        ? Object.fromEntries(columns.map(column => [column.field, column.label]))
                        : buildHeaders(profile),
                    ...options
                }
            }

            const result = await postMeFile(`${endpoint}/export`, body)

            if (result?.error) {
                setMessage({
                    show: true,
                    type: 'error',
                    title: t('export.failed.title'),
                    message: result.error.error || t('export.failed.message')
                })
                return result
            }

            saveFile(result.file, result.filename)
            setMessage({ show: false })
            return result

        } catch (error) {
            console.error('Export error:', error.message)
            setMessage({ show: true, type: 'error', title: t('export.failed.title'), message: error.message })
            return { error }
        } finally {
            setIsExporting(false)
        }
    }, [endpoint, buildHeaders, currentColumns, postMeFile, setMessage, t])

    return { runExport, isExporting, formats, profiles }
}

/**
 * Hand the blob to the browser. The object URL is revoked on the next tick
 * rather than immediately: revoking before the download starts cancels it.
 */
const saveFile = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(url), 0)
}

export default useListExport
